import React, { useEffect, useState, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function MultiModelEquityCurve({ period = 'monthly', year, month }) {
  const chartRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredModel, setHoveredModel] = useState(null);

  // Fetch equity curve data
  const fetchData = async () => {
    try {
      setLoading(true);
      const now = new Date();
      const queryYear = year || now.getUTCFullYear();
      const queryMonth = month || now.getUTCMonth() + 1;

      let url = `/api/leaderboard/equity-curves?period=${period}&year=${queryYear}`;
      if (period === 'monthly') {
        url += `&month=${queryMonth}`;
      }

      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch equity curves');

      const result = await res.json();
      setData(result);
      setError(null);
    } catch (err) {
      console.error('Error fetching equity curves:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount and when period changes
  useEffect(() => {
    fetchData();
  }, [period, year, month]);

  // Listen for trade_closed events for real-time updates
  useEffect(() => {
    const handleTradeClosed = () => {
      fetchData();
    };

    window.addEventListener('trade_closed', handleTradeClosed);
    return () => window.removeEventListener('trade_closed', handleTradeClosed);
  }, [period, year, month]);

  // Loading state
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse text-dark-text-secondary">Loading equity curves...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-80 flex items-center justify-center text-negative">
        Error: {error}
      </div>
    );
  }

  // Empty state - no trades
  if (!data?.hasData) {
    return (
      <div className="h-80 flex flex-col items-center justify-center text-center">
        <svg className="w-12 h-12 mb-4 text-dark-text-muted opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18h18M7 16l4-4 4 4 4-6" />
        </svg>
        <span className="text-sm text-dark-text-muted">No completed trades in this period</span>
        <span className="text-xs text-dark-text-muted mt-1">
          Equity curves will appear as models close positions
        </span>
      </div>
    );
  }

  // Build unified timeline from all models
  const allTimestamps = new Set();
  data.models.forEach(model => {
    model.data.forEach(d => allTimestamps.add(d.time));
  });
  const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a - b);

  // Format timestamps for display
  const labels = sortedTimestamps.map(ts => {
    const date = new Date(ts * 1000);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  });

  // Build datasets for Chart.js
  const datasets = data.models
    .filter(model => model.data.length > 0)
    .map(model => {
      // Create a map of timestamp -> balance for this model
      const balanceMap = new Map();
      model.data.forEach(d => balanceMap.set(d.time, d.balance));

      // Fill in values for each timestamp (use previous value if not present)
      let lastBalance = data.starting_balance;
      const values = sortedTimestamps.map(ts => {
        if (balanceMap.has(ts)) {
          lastBalance = balanceMap.get(ts);
        }
        return lastBalance;
      });

      const isHovered = hoveredModel === model.id;
      const isDimmed = hoveredModel !== null && hoveredModel !== model.id;

      return {
        label: model.name,
        data: values,
        borderColor: model.color,
        backgroundColor: model.color + '20',
        borderWidth: isHovered ? 3 : isDimmed ? 1 : 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.1,
        fill: false,
        modelId: model.id
      };
    });

  // Add baseline (starting balance) as dashed line
  datasets.unshift({
    label: 'Starting Balance',
    data: sortedTimestamps.map(() => data.starting_balance),
    borderColor: '#6B7280',
    borderWidth: 1,
    borderDash: [5, 5],
    pointRadius: 0,
    pointHoverRadius: 0,
    fill: false,
    hidden: false
  });

  const chartData = {
    labels,
    datasets
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: false // We use our own legend
      },
      tooltip: {
        backgroundColor: '#1e2530',
        titleColor: '#e6edf3',
        bodyColor: '#e6edf3',
        borderColor: '#2d3748',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const pnl = value - data.starting_balance;
            const sign = pnl >= 0 ? '+' : '';
            return `${context.dataset.label}: $${value.toLocaleString()} (${sign}$${pnl.toLocaleString()})`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(212, 160, 18, 0.08)',
          drawBorder: false
        },
        ticks: {
          color: '#9ca3af',
          maxRotation: 0,
          maxTicksLimit: 8
        }
      },
      y: {
        grid: {
          color: 'rgba(212, 160, 18, 0.08)',
          drawBorder: false
        },
        ticks: {
          color: '#9ca3af',
          callback: (value) => '$' + value.toLocaleString()
        }
      }
    }
  };

  return (
    <div>
      {/* Legend */}
      <div className="flex flex-wrap gap-2 mb-4">
        {data.models
          .filter(model => model.data.length > 0)
          .map(model => (
            <LegendItem
              key={model.id}
              model={model}
              isHovered={hoveredModel === model.id}
              onMouseEnter={() => setHoveredModel(model.id)}
              onMouseLeave={() => setHoveredModel(null)}
            />
          ))}
      </div>

      {/* Chart */}
      <div className="h-80">
        <Line ref={chartRef} data={chartData} options={options} />
      </div>
    </div>
  );
}

function LegendItem({ model, isHovered, onMouseEnter, onMouseLeave }) {
  // Calculate current balance (last data point)
  const currentBalance = model.data.length > 0
    ? model.data[model.data.length - 1].balance
    : 100000;
  const pnl = currentBalance - 100000;
  const isPositive = pnl >= 0;

  return (
    <button
      className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-all ${
        isHovered ? 'bg-dark-tertiary' : 'bg-dark-surface hover:bg-dark-tertiary/50'
      }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <span
        className="w-3 h-3 rounded-full"
        style={{ backgroundColor: model.color }}
      />
      <span className="text-xs font-medium text-dark-text-secondary">
        {model.name}
      </span>
      <span className={`text-xs font-mono ${isPositive ? 'text-positive' : 'text-negative'}`}>
        {isPositive ? '+' : ''}{pnl.toFixed(0)}
      </span>
    </button>
  );
}

export default MultiModelEquityCurve;
